<script setup lang="ts">
import { useVModel } from '@vueuse/core'

import AudienceDisclosure from '@/views/audience/components/AudienceDisclosure.vue'
import AudienceCountryBlock from '@/views/audience/AudiencePage/AudienceCountryBlock.vue'
import type { Option } from '@/components/base/BaseMultiSelect.vue'
import type { AudienceRule, RulePrimitive, RuleValue } from '@/types/types'
import type { HighlightInfoFn } from '../composables/rulesMatcher'
import { capitalize } from '@/utils/string'

const { gendersList } = useCustomer()

const props = defineProps<{
  rules: Record<string, AudienceRule>
  classes: any
  disabled: boolean
  getHighlightInfo: HighlightInfoFn
  hideStoreScope?: boolean
  hasStoreScope?: boolean
  additionalStoreOptions?: { value: number; label: string }[]
}>()
const { t } = useI18n()
const emit = defineEmits(['update:rules', 'hasRulesError'])

const rules = useVModel(props, 'rules', emit)

const storeIds = defineModel<number[]>('storeIds', { default: () => [] })

const highlight = (key: string) => props.getHighlightInfo(key).hasDefined

const activeFiltersCount = computed(() => {
  const keys = [
    'gender',
    'age',
    'firstName',
    'lastName',
    'email',
    'birthDate',
    'optin',
    'hasOptinEmail',
    'country',
    'city'
  ]
  const ruleCount = keys.filter(
    (k) => Array.isArray(rules.value[k]?.value) && (rules.value[k].value as unknown[]).length > 0
  ).length
  return ruleCount + (props.hasStoreScope ? 1 : 0)
})

const UNKNOWN_GENDER = '__unknown__'

const genderMultiOptions = computed<Option[]>(() =>
  gendersList.map((g) => ({ value: g.slug ?? UNKNOWN_GENDER, label: g.text }))
)

const getGenderValues = (): string[] => {
  const v = rules.value.gender?.value
  if (!Array.isArray(v)) return []
  return (v as RulePrimitive[]).map((item) => (item === null ? UNKNOWN_GENDER : String(item)))
}

const setGenderValues = (vals: string[]) => {
  if (!rules.value.gender) return
  const mapped = vals.map((v) => (v === UNKNOWN_GENDER ? null : v)) as RuleValue
  rules.value.gender = { ...rules.value.gender, value: mapped }
}

const presenceOptions = computed(() => [
  { id: 'exist', text: t('pages.audience.audienceCreatePage.block.profil.presence.filled') },
  { id: 'no_exist', text: t('pages.audience.audienceCreatePage.block.profil.presence.empty') }
])

const getPresenceOp = (rule: AudienceRule | undefined): string | null => {
  if (!rule || !Array.isArray(rule.value) || (rule.value as unknown[]).length === 0) return null
  return rule.operator
}

const setPresenceOp = (ruleKey: string, operator: string | null | undefined) => {
  if (!rules.value[ruleKey]) return
  rules.value[ruleKey] = {
    ...rules.value[ruleKey],
    operator: operator ?? rules.value[ruleKey].operator ?? 'exist',
    value: operator ? ([1] as RuleValue) : []
  }
}

const ageOperatorOptions = computed(() => [
  ...(['eq', 'gt', 'lt', 'lte', 'gte', 'between'] as const).map((op) => ({
    id: op as string,
    text: capitalize(t(`pages.audience.operators.${op}`))
  })),
  { id: 'exist', text: capitalize(t('pages.audience.operators.in')) }
])

const NUMERIC_AGE_OPS = ['eq', 'gt', 'lt', 'lte', 'gte', 'between']

const hasBirthDateValue = computed(
  () => Array.isArray(rules.value.birthDate?.value) && (rules.value.birthDate.value as unknown[]).length > 0
)

const isAgePresenceMode = computed(
  () => !NUMERIC_AGE_OPS.includes(rules.value.age?.operator ?? '') || hasBirthDateValue.value
)

const currentAgeOp = computed<string | null>(() => {
  if (isAgePresenceMode.value) return 'exist'
  return rules.value.age.operator ?? null
})

const setAgeOperator = (op: string | null | undefined) => {
  if (!op) {
    rules.value.age = { ...rules.value.age, operator: 'exist', value: [] }
    rules.value.birthDate = { ...rules.value.birthDate, value: [] }
    return
  }
  if (op === 'exist') {
    rules.value.age = { ...rules.value.age, operator: 'exist', value: [] }
    setPresenceOp('birthDate', 'exist')
    return
  }
  rules.value.birthDate = { ...rules.value.birthDate, value: [] }
  const realVals = ((rules.value.age.value as RulePrimitive[]) ?? []).filter((v) => v !== null)
  const [a, b] = realVals
  rules.value.age = {
    ...rules.value.age,
    operator: op,
    value: op === 'between' ? (a !== undefined ? [a, b ?? null] : []) : a !== undefined ? [a] : []
  }
}

const presenceInfoFields = ['firstName', 'lastName', 'email'] as const

const consentMultiOptions = computed<Option[]>(() => [
  { value: 'true', label: t('pages.audience.audienceCreatePage.block.profil.optin.true') },
  { value: 'false', label: t('pages.audience.audienceCreatePage.block.profil.optin.false') },
  { value: 'null', label: t('pages.audience.audienceCreatePage.block.profil.optin.null') }
])

const getConsentValues = (rule: AudienceRule | undefined): string[] => {
  if (!rule || !Array.isArray(rule.value)) return []
  return (rule.value as unknown[]).map((val) => (val === null ? 'null' : String(val)))
}

const setConsentValues = (ruleKey: string, strVals: string[]) => {
  if (!rules.value[ruleKey]) return
  const mapped: RulePrimitive[] = strVals.map((v) => {
    if (v === 'null') return null
    return v === 'true'
  })
  rules.value[ruleKey] = { ...rules.value[ruleKey], value: mapped as RuleValue }
}
</script>

<template>
  <AudienceDisclosure
    :title="t('pages.audience.audienceCreatePage.block.profil.title')"
    :subtitle="t('pages.audience.audienceCreatePage.block.profil.description')"
    icon="IconoirUser"
    :active-filters-count="activeFiltersCount"
  >
    <div :class="[classes.rowGrid, '-mt-5']">
      <div
        class="col-span-12 -mx-5 bg-muted px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
      >
        {{ t('pages.audience.audienceCreatePage.block.profil.sections.location') }}
      </div>
    </div>

    <div v-if="!hideStoreScope" :class="classes.rowGrid">
      <label :class="[classes.label, 'flex items-center gap-1']">
        <span>{{ t('pages.audience.audienceCreatePage.block.profil.store.label') }}</span>
        <AppTooltip
          class="inline-flex"
          :text="t('pages.audience.audienceCreatePage.block.profil.store.tooltip')"
          tooltip-position="bottom"
          width="w-72"
        >
          <BaseIcon name="IconoirInfoCircle" class="h-4 w-4 cursor-pointer text-foreground-secondary" />
        </AppTooltip>
      </label>
      <div :class="classes.inputs">
        <div class="flex h-9 items-center text-sm font-medium text-foreground">
          {{ t('pages.audience.operators.in') }}
        </div>
        <MultiSelectStores
          v-model="storeIds"
          :has-label="false"
          :show-all-selected-text="false"
          class="sm:w-full"
          :additional-store-options="additionalStoreOptions"
          :disabled="disabled"
        />
      </div>
    </div>

    <AudienceCountryBlock
      :rules="rules"
      :classes="classes"
      :disabled="disabled"
      :get-highlight-info="getHighlightInfo"
    />

    <div :class="[classes.rowGrid, { 'rounded-lg bg-blue-50/30 p-3 ring-1 ring-blue-600': highlight('city') }]">
      <label :class="classes.label">
        {{ t('pages.audience.audienceCreatePage.block.profil.city.label') }}
      </label>
      <div :class="classes.inputs">
        <div class="flex h-9 items-center text-sm font-medium text-foreground">
          {{ t('pages.audience.operators.in') }}
        </div>
        <ComboboxSelect
          :model-value="getPresenceOp(rules.city)"
          value-key="id"
          label-key="text"
          clearable
          :disabled="disabled"
          :options="presenceOptions"
          :search-threshold="Infinity"
          trigger-class="w-full"
          :placeholder="t('pages.audience.audienceCreatePage.block.profil.presence.placeholder')"
          @update:model-value="setPresenceOp('city', $event as string | null | undefined)"
        />
      </div>
    </div>
    <div :class="classes.rowGrid">
      <div
        class="col-span-12 -mx-5 bg-muted px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
      >
        {{ t('pages.audience.audienceCreatePage.block.profil.sections.infos') }}
      </div>
    </div>

    <div :class="[classes.rowGrid, { 'rounded-lg bg-blue-50/30 p-3 ring-1 ring-blue-600': highlight('gender') }]">
      <label :class="classes.label">
        {{ t('pages.audience.audienceCreatePage.block.profil.genders.label') }}
      </label>
      <div :class="classes.inputs">
        <div class="flex h-9 items-center text-sm font-medium text-foreground">
          {{ t('pages.audience.operators.in') }}
        </div>
        <BaseMultiSelect
          :model-value="getGenderValues()"
          :disabled="disabled"
          :options="genderMultiOptions"
          :show-toggle-all="false"
          clearable
          :max-selected-labels="2"
          :placeholder="t('pages.audience.audienceCreatePage.block.profil.presence.placeholder')"
          @update:model-value="setGenderValues($event as string[])"
        />
      </div>
    </div>

    <div
      :class="[
        classes.rowGrid,
        { 'rounded-lg bg-blue-50/30 p-3 ring-1 ring-blue-600': highlight('age') || highlight('birthDate') }
      ]"
    >
      <label :class="classes.label">
        {{ t('pages.audience.audienceCreatePage.block.profil.age.label') }}
      </label>
      <div :class="classes.inputs">
        <ComboboxSelect
          :model-value="currentAgeOp"
          value-key="id"
          label-key="text"
          clearable
          :disabled="disabled"
          :search-threshold="Infinity"
          trigger-class="w-full"
          :placeholder="t('pages.audience.audienceCreatePage.block.profil.presence.placeholder')"
          :options="ageOperatorOptions"
          @update:model-value="setAgeOperator($event as string | null | undefined)"
        />
        <ComboboxSelect
          v-if="isAgePresenceMode"
          :model-value="getPresenceOp(rules.birthDate)"
          value-key="id"
          label-key="text"
          clearable
          :disabled="disabled"
          :options="presenceOptions"
          :search-threshold="Infinity"
          trigger-class="w-full"
          :placeholder="t('pages.audience.audienceCreatePage.block.profil.presence.placeholder')"
          @update:model-value="setPresenceOp('birthDate', $event as string | null | undefined)"
        />
        <div v-else-if="rules.age?.operator === 'between'" class="flex items-center gap-1.5">
          <AppFormInput
            id="age-first-value"
            v-model="rules.age.value[0]"
            type="number"
            :disabled="disabled"
            :min="0"
            :max="120"
            class="min-w-0 flex-1"
          />
          <span class="shrink-0 text-xs uppercase text-muted-foreground">{{ t('global.words.and') }}</span>
          <AppFormInput
            id="age-second-value"
            v-model="rules.age.value[1]"
            type="number"
            :disabled="disabled"
            :min="0"
            :max="120"
            class="min-w-0 flex-1"
          />
        </div>
        <AppFormInput
          v-else-if="currentAgeOp"
          id="age-value"
          v-model="rules.age.value[0]"
          type="number"
          :disabled="disabled"
          :min="0"
          :max="120"
          class="w-full"
        />
        <div v-else />
      </div>
    </div>

    <template v-for="field in presenceInfoFields" :key="field">
      <div :class="[classes.rowGrid, { 'rounded-lg bg-blue-50/30 p-3 ring-1 ring-blue-600': highlight(field) }]">
        <label :class="classes.label">
          {{ t(`pages.audience.audienceCreatePage.block.profil.${field}.label`) }}
        </label>
        <div :class="classes.inputs">
          <div class="flex h-9 items-center text-sm font-medium text-foreground">
            {{ t('pages.audience.operators.in') }}
          </div>
          <ComboboxSelect
            :model-value="getPresenceOp(rules[field])"
            value-key="id"
            label-key="text"
            clearable
            :disabled="disabled"
            :options="presenceOptions"
            :search-threshold="Infinity"
            trigger-class="w-full"
            :placeholder="t('pages.audience.audienceCreatePage.block.profil.presence.placeholder')"
            @update:model-value="setPresenceOp(field, $event as string | null | undefined)"
          />
        </div>
      </div>
    </template>

    <div :class="classes.rowGrid">
      <div
        class="col-span-12 -mx-5 bg-muted px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
      >
        {{ t('pages.audience.audienceCreatePage.block.profil.sections.consent') }}
      </div>
    </div>

    <div :class="[classes.rowGrid, { 'rounded-lg bg-blue-50/30 p-3 ring-1 ring-blue-600': highlight('optin') }]">
      <label :class="classes.label">
        {{ t('pages.audience.audienceCreatePage.block.profil.optin.label') }}
      </label>
      <div :class="classes.inputs">
        <div class="flex h-9 items-center text-sm font-medium text-foreground">
          {{ t('pages.audience.operators.in') }}
        </div>
        <BaseMultiSelect
          :model-value="getConsentValues(rules.optin)"
          :disabled="disabled"
          :options="consentMultiOptions"
          :show-toggle-all="false"
          :filter="false"
          :max-selected-labels="1"
          clearable
          :placeholder="t('pages.audience.audienceCreatePage.block.profil.presence.placeholder')"
          @update:model-value="setConsentValues('optin', $event as string[])"
        />
      </div>
    </div>

    <div
      :class="[classes.rowGrid, { 'rounded-lg bg-blue-50/30 p-3 ring-1 ring-blue-600': highlight('hasOptinEmail') }]"
    >
      <label :class="classes.label">
        {{ t('pages.audience.audienceCreatePage.block.profil.hasOptinEmail.label') }}
      </label>
      <div :class="classes.inputs">
        <div class="flex h-9 items-center text-sm font-medium text-foreground">
          {{ t('pages.audience.operators.in') }}
        </div>
        <BaseMultiSelect
          :model-value="getConsentValues(rules.hasOptinEmail)"
          :disabled="disabled"
          :options="consentMultiOptions"
          :show-toggle-all="false"
          :filter="false"
          clearable
          :placeholder="t('pages.audience.audienceCreatePage.block.profil.presence.placeholder')"
          @update:model-value="setConsentValues('hasOptinEmail', $event as string[])"
        />
      </div>
    </div>
  </AudienceDisclosure>
</template>
